import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Avatar,
  Divider,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import { RootState } from '../../store';
import {
  CheckCircle as CompletedIcon,
  RadioButtonUnchecked as PendingIcon,
  Schedule as InProgressIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Notes as NotesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  QrCode as QrCodeIcon,
  Engineering as EngineeringIcon,
  LocalShipping as DispatchIcon,
  Build as BuildIcon,
  Palette as CoatingIcon,
  CheckCircleOutline as QualityIcon,
} from '@mui/icons-material';
import { formatDate, formatTime, formatStatusText } from '../../utils/formatters';

interface TimelineEvent {
  id: string;
  stage: string;
  status: 'completed' | 'in_progress' | 'pending' | 'skipped';
  user_id: string;
  user_name: string;
  timestamp: string;
  notes?: string;
  duration_hours?: number;
  qr_code?: string;
  stage_order: number;
  previous_stage?: string;
  next_stage?: string;
}

interface OrderTimelineProps {
  orderId: string;
  orderNumber: string;
  itemId?: string;
  itemType?: string;
  events: TimelineEvent[];
  onClose?: () => void;
}

const OrderTimeline: React.FC<OrderTimelineProps> = ({
  orderId,
  orderNumber,
  itemId,
  itemType,
  events,
  onClose,
}) => {
  const timelineSettings = useSelector((state: RootState) => state.timeline);
  const [expandedEvents, setExpandedEvents] = React.useState<Set<string>>(new Set());

  const toggleEventExpansion = (eventId: string) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const getStageIcon = (stage: string) => {
    const stageLower = stage.toLowerCase();
    if (stageLower.includes('cutting') || stageLower.includes('fabrication')) {
      return <BuildIcon />;
    } else if (stageLower.includes('assembly')) {
      return <EngineeringIcon />;
    } else if (stageLower.includes('welding')) {
      return <EngineeringIcon />;
    } else if (stageLower.includes('grinding')) {
      return <BuildIcon />;
    } else if (stageLower.includes('coating') || stageLower.includes('painting')) {
      return <CoatingIcon />;
    } else if (stageLower.includes('quality')) {
      return <QualityIcon />;
    } else if (stageLower.includes('dispatch')) {
      return <DispatchIcon />;
    }
    return <EngineeringIcon />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon color="success" />;
      case 'in_progress':
        return <InProgressIcon color="primary" />;
      case 'pending':
        return <PendingIcon color="action" />;
      case 'skipped':
        return <PendingIcon color="disabled" />;
      default:
        return <PendingIcon color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'skipped':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStageColor = (stage: string) => {
    const stageLower = stage.toLowerCase();
    if (stageLower.includes('cutting') || stageLower.includes('fabrication')) {
      return 'primary';
    } else if (stageLower.includes('assembly')) {
      return 'secondary';
    } else if (stageLower.includes('welding')) {
      return 'error';
    } else if (stageLower.includes('grinding')) {
      return 'warning';
    } else if (stageLower.includes('coating') || stageLower.includes('painting')) {
      return 'info';
    } else if (stageLower.includes('quality')) {
      return 'success';
    } else if (stageLower.includes('dispatch')) {
      return 'default';
    }
    return 'primary';
  };

  // Sort events by stage order and timestamp
  const sortedEvents = [...events].sort((a, b) => {
    if (a.stage_order !== b.stage_order) {
      return a.stage_order - b.stage_order;
    }
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  // Group events by stage
  const groupedEvents = sortedEvents.reduce((acc, event) => {
    if (!acc[event.stage]) {
      acc[event.stage] = [];
    }
    acc[event.stage].push(event);
    return acc;
  }, {} as Record<string, TimelineEvent[]>);

  const stages = Object.keys(groupedEvents).sort((a, b) => {
    const aOrder = groupedEvents[a][0]?.stage_order || 0;
    const bOrder = groupedEvents[b][0]?.stage_order || 0;
    return aOrder - bOrder;
  });

  const getActiveStep = () => {
    const completedStages = stages.filter(stage => 
      groupedEvents[stage].some(event => event.status === 'completed')
    );
    return completedStages.length;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Order Timeline
          </Typography>
          {onClose && (
            <IconButton onClick={onClose} size="small">
              ✕
            </IconButton>
          )}
        </Box>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Order: {orderNumber}
          </Typography>
          {itemId && (
            <Typography variant="body2" color="text.secondary">
              Part: {itemId.slice(-8)} {itemType && `(${itemType})`}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Total Events: {events.length}
          </Typography>
        </Stack>
      </Box>

      {/* Timeline Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stepper activeStep={getActiveStep()} orientation="vertical" sx={{ pl: 0 }}>
          {stages.map((stage, stageIndex) => {
            const stageEvents = groupedEvents[stage];
            const latestEvent = stageEvents[stageEvents.length - 1];
            const isCompleted = latestEvent?.status === 'completed';
            const isInProgress = latestEvent?.status === 'in_progress';
            const isPending = latestEvent?.status === 'pending';

            return (
              <Step key={stage} completed={isCompleted} active={isInProgress}>
                <StepLabel
                  icon={getStageIcon(stage)}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '1rem',
                      fontWeight: isInProgress ? 600 : 400,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: isInProgress ? 600 : 400 }}>
                      {stage === 'Dispatch' ? 'Ready To Dispatch' : stage}
                    </Typography>
                    <Chip
                      label={formatStatusText(latestEvent?.status || 'pending')}
                      color={getStatusColor(latestEvent?.status || 'pending') as any}
                      size="small"
                      icon={getStatusIcon(latestEvent?.status || 'pending')}
                    />
                    {latestEvent?.duration_hours && (
                      <Chip
                        label={`${latestEvent.duration_hours}h`}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ ml: 4, mb: 2 }}>
                    {/* Stage Summary */}
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        mb: 2,
                        bgcolor: isCompleted ? 'success.light' : isInProgress ? 'primary.light' : 'grey.50',
                        opacity: isCompleted ? 0.8 : 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Stage Summary
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleEventExpansion(stage)}
                        >
                          {expandedEvents.has(stage) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {latestEvent?.user_name || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {latestEvent?.timestamp ? formatDate(latestEvent.timestamp) : 'Not started'}
                            {latestEvent?.timestamp && ` at ${formatTime(latestEvent.timestamp)}`}
                          </Typography>
                        </Box>
                        {timelineSettings.showQRCodes && latestEvent?.qr_code && (
                          <Tooltip title={`QR Code: ${latestEvent.qr_code}`}>
                            <Chip
                              icon={<QrCodeIcon />}
                              label="QR Scanned"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </Paper>

                    {/* Detailed Events */}
                    <Collapse in={expandedEvents.has(stage)} >
                      <List dense>
                        {stageEvents.map((event, eventIndex) => (
                          <React.Fragment key={event.id}>
                            <ListItem sx={{ pl: 0 }}>
                              <ListItemAvatar>
                                <Avatar 
                                  sx={{ 
                                    width: 24, 
                                    height: 24,
                                    bgcolor: getStatusColor(event.status) + '.main',
                                  }}
                                >
                                  {getStatusIcon(event.status)}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {event.stage}
                                    </Typography>
                                    <Chip
                                      label={formatStatusText(event.status)}
                                      color={getStatusColor(event.status) as any}
                                      size="small"
                                      variant="outlined"
                                    />
                                    {timelineSettings.showDurationTracking && event.duration_hours && (
                                      <Chip
                                        label={`${event.duration_hours}h`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      By {event.user_name} • {formatDate(event.timestamp)} at {formatTime(event.timestamp)}
                                    </Typography>
                                    {timelineSettings.showNotes && event.notes && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                          <NotesIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                          {event.notes}
                                        </Typography>
                                      </Box>
                                    )}
                                    {timelineSettings.showQRCodes && event.qr_code && (
                                      <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                          icon={<QrCodeIcon />}
                                          label={`QR: ${event.qr_code.slice(-8)}`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            {eventIndex < stageEvents.length - 1 && <Divider sx={{ ml: 3 }} />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                </StepContent>
              </Step>
            );
          })}
        </Stepper>

        {/* Timeline Summary */}
        {timelineSettings.showProgressBars && (
          <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Timeline Summary
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip
                label={`${events.filter(e => e.status === 'completed').length} Completed`}
                color="success"
                size="small"
                icon={<CompletedIcon />}
              />
              <Chip
                label={`${events.filter(e => e.status === 'in_progress').length} In Progress`}
                color="primary"
                size="small"
                icon={<InProgressIcon />}
              />
              <Chip
                label={`${events.filter(e => e.status === 'pending').length} Pending`}
                color="warning"
                size="small"
                icon={<PendingIcon />}
              />
              {events.filter(e => e.status === 'skipped').length > 0 && (
                <Chip
                  label={`${events.filter(e => e.status === 'skipped').length} Skipped`}
                  color="default"
                  size="small"
                  icon={<PendingIcon />}
                />
              )}
            </Stack>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default OrderTimeline;
